// SDK bootstrap — fetches per-shop config from the backend on every page
// load. CloudFront caches /sdk-config at the edge for 5 minutes, so repeat
// visits from the same region resolve at the CDN with no origin hit.
//
// Why no localStorage cache:
//
//   The backend lazily revalidates the merchant's GoKwik token on every
//   /sdk-config call. If GoKwik says the token is invalid (merchant
//   uninstalled), the next /sdk-config returns 404. With a 24-hour
//   localStorage cache we'd happily keep firing analytics for a day after
//   the merchant uninstalled.
//
//   CloudFront's 5-minute edge cache is the right cache layer — short
//   enough that uninstalls propagate fast, shared enough that origin load
//   stays bounded, and free (versus localStorage which is per-device).
//
// The API base URL is injected at build time from VITE_API_BASE_URL.
// Vite replaces import.meta.env.VITE_* with string literals during the
// IIFE build, so the deployed bundle still has the URL baked in — but
// different environments (prod, staging, local) can now build against
// different backends without touching source code.
// Set it in .env (local), .env.production (prod build), etc.

import type { ShopifyPixelConfig } from './types';

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  throw new Error(
    '[shopify-pixel-sdk] VITE_API_BASE_URL is not set. ' +
    'Create a .env file with VITE_API_BASE_URL=https://your-backend.example.com'
  );
}

const FETCH_TIMEOUT_MS = 5_000;
// Retry on transient network failures (5xx, abort). 4xx including 404
// short-circuit immediately — no point retrying a configuration that the
// backend says doesn't exist.
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;

class RetryableError extends Error {}
class TerminalError extends Error {}

async function fetchOnce(): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(`${API_BASE_URL}/api/v1/sdk-config`, {
      method: 'GET',
      // Don't send cookies — the endpoint is origin-keyed, not user-keyed,
      // and `credentials: 'omit'` keeps the response cacheable by CDNs.
      credentials: 'omit',
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Resolve the SDK's per-shop config from the backend. Returns null if
 * the merchant doesn't exist, has no config, has uninstalled the app,
 * or the network call fails after all retries.
 *
 * Callers should treat null as "skip dispatch this page load" — there's
 * no point firing analytics with a missing or stale config.
 */
export async function bootstrapConfig(): Promise<ShopifyPixelConfig | null> {
  let lastErr: Error | null = null;

  for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    try {
      const res = await fetchOnce();

      if (res.status >= 400 && res.status < 500) {
        // 404, 403, 401 — config is genuinely unavailable. No retry.
        // eslint-disable-next-line no-console
        console.warn(
          `[shopify-pixel] sdk-config ${res.status} ${res.statusText} — SDK disabled for this page load`,
        );
        return null;
      }
      if (!res.ok) {
        throw new RetryableError(`sdk-config: ${res.status} ${res.statusText}`);
      }

      const body = (await res.json()) as Record<string, unknown>;
      // Backend may wrap responses; accept both shapes.
      const data = (body.data ?? body) as Partial<ShopifyPixelConfig>;

      // shopifyShopId is the actual Shopify shop ID and the only correct
      // value to pass to monorail. Until the merchant fills it in, we have
      // nothing useful to send — better to bail than to ship merchantId
      // (GoKwik's identifier) and pollute the merchant's analytics with
      // bogus `shopId` values.
      const shopifyShopId = (data as { shopifyShopId?: unknown }).shopifyShopId;
      if (!shopifyShopId) {
        // eslint-disable-next-line no-console
        console.warn(
          '[shopify-pixel] sdk-config has no shopifyShopId yet — merchant must complete onboarding in the dashboard',
        );
        return null;
      }

      return {
        // hydrogen-react's API names this `shopId`; we map our shopifyShopId
        // onto that name here so the rest of the SDK stays vendor-neutral.
        shopId: String(shopifyShopId),
        domain: data.domain ? String(data.domain) : '',
        merchantName: data.merchantName ? String(data.merchantName) : '',
        currency: data.currency ? String(data.currency) : undefined,
        acceptedLanguage: data.acceptedLanguage,
        shopifySalesChannel: data.shopifySalesChannel,
        // The backend returns the merchant's Headless storefront id as
        // `shopifyStorefrontId`; hydrogen-react calls it `storefrontId`.
        storefrontId:
          (data as { shopifyStorefrontId?: unknown }).shopifyStorefrontId != null
            ? String((data as { shopifyStorefrontId?: unknown }).shopifyStorefrontId)
            : data.storefrontId,
        cartMirror: data.cartMirror === true,
        shopMonorailDomain: data.shopMonorailDomain,
        hasUserConsent: data.hasUserConsent,
        gdprEnforced: data.gdprEnforced,
        ccpaEnforced: data.ccpaEnforced,
      };
    } catch (err) {
      if (err instanceof TerminalError) {
        return null;
      }
      lastErr = err as Error;
      if (attempt < MAX_RETRY_ATTEMPTS) {
        // Exponential backoff with jitter: 500ms, ~1s, ~2s
        const delay =
          RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) +
          Math.floor(Math.random() * 250);
        await sleep(delay);
        continue;
      }
    }
  }

  // eslint-disable-next-line no-console
  console.warn('[shopify-pixel] sdk-config bootstrap failed:', lastErr);
  return null;
}
